import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';
import type { SocialGlyphToken } from 'volto-ministerio-publico-federal/components/Icons/social';

/** As plataformas de áudio que o portal publica. */
export const PLATAFORMAS = ['spotify', 'deezer', 'amazonmusic'] as const;

export type Plataforma = (typeof PLATAFORMAS)[number];

/** Título padrão da faixa, também usado como `placeholder` no formulário. */
export const RADIO_TITULO = 'Rádio e podcast';

/**
 * Referência como o `ObjectBrowserWidget` grava.
 *
 * O widget devolve SEMPRE uma lista de objetos — inclusive quando o editor
 * digita um endereço externo, caso em que ele monta `{'@id': url, title: url}`
 * (ver `onSubmitManualLink` no widget do core). Tratar o campo como string é o
 * que fazia o bloco quebrar com "trim is not a function" assim que alguém
 * escolhia um arquivo de verdade.
 */
export interface ReferenciaEscolhida {
  '@id'?: string;
  title?: string;
}

/** Valor de um campo `object_browser`, nas duas formas que aparecem na prática. */
export type ValorEscolhido = string | ReferenciaEscolhida[] | undefined;

/** Item como o editor grava. */
export interface EpisodioItem {
  '@id'?: string;
  plataforma?: string;
  data?: string;
  titulo?: string;
  descricao?: string;
  /** Arquivo do portal ou endereço externo do áudio. */
  audio?: ValorEscolhido;
  /** Página do episódio na plataforma. */
  link?: ValorEscolhido;
}

/**
 * Endereço cru de um campo `object_browser`.
 *
 * Aceita também string simples: o `SeletorDeEpisodios` grava o `<enclosure>`
 * do feed direto, e conteúdo vindo de importação ou JSON escrito à mão pode
 * chegar em qualquer uma das duas formas.
 */
export const hrefEscolhido = (valor: ValorEscolhido): string => {
  if (typeof valor === 'string') return valor.trim();
  const primeiro = Array.isArray(valor) ? valor[0] : undefined;
  return (primeiro?.['@id'] ?? '').trim();
};

/** Dado gravado pelo bloco. */
export interface RadioData {
  '@type'?: string;
  titulo?: string;
  exibirTitulo?: boolean;
  /**
   * Endereço RSS do podcast. Não é lido em tempo de render — serve só para o
   * editor escolher episódios da lista na barra lateral, e o que ele escolhe é
   * gravado em `episodios`. Ver `SeletorDeEpisodios`.
   */
  feed?: string;
  episodios?: EpisodioItem[];
}

/** Episódio já resolvido para a tela. */
export interface EpisodioCard {
  key: string;
  plataforma?: SocialGlyphToken;
  data: string;
  titulo: string;
  descricao: string;
  /** `src` pronto para o `<audio>`. */
  audio: string;
  link: string;
  /**
   * Índice na lista CRUA do bloco — é ele que o lápis do modo de edição manda
   * para o `ObjectListWidget`. Ver `Imprensa/helpers.ts#toContatosIndexados`.
   */
  sourceIndex: number;
}

/**
 * `src` do áudio.
 *
 * Um arquivo do portal é um conteúdo `File`, e o binário mora em
 * `<caminho>/@@download/file` — a mesma regra que o bloco Vídeo do core aplica
 * ao ramo `.mp4` (`customizations/.../Video/Body.jsx`). Endereço externo passa
 * intacto, e endereço que já aponta para o download não ganha o sufixo duas
 * vezes.
 */
export const audioSrc = (valor: ValorEscolhido): string => {
  const url = hrefEscolhido(valor);
  if (!url) return '';
  if (!isInternalURL(url)) return url;
  const interno = flattenToAppURL(url);
  return interno.includes('@@download')
    ? interno
    : `${interno}/@@download/file`;
};

/**
 * Segundos em `m:ss`.
 *
 * `duration` só existe depois do evento `loadedmetadata`, e num stream sem
 * tamanho declarado ele nunca sai de `Infinity` — nos dois casos o card mostra
 * `0:00` em vez de `NaN:NaN`.
 */
export const formatTempo = (segundos?: number): string => {
  if (!Number.isFinite(segundos) || (segundos as number) < 0) return '0:00';
  const total = Math.floor(segundos as number);
  const minutos = Math.floor(total / 60);
  const resto = total % 60;
  return `${minutos}:${String(resto).padStart(2, '0')}`;
};

const isPlataforma = (valor?: string): valor is Plataforma =>
  !!valor && (PLATAFORMAS as readonly string[]).includes(valor);

/**
 * Converte a lista crua do editor nos cards da tela, descartando o que não dá
 * para tocar: episódio sem arquivo de áudio.
 */
export const toEpisodios = (episodios?: EpisodioItem[]): EpisodioCard[] =>
  (episodios ?? [])
    .map((episodio, index) => ({
      episodio,
      index,
      audio: audioSrc(episodio?.audio),
    }))
    .filter(({ audio }) => Boolean(audio))
    .map(({ episodio, index, audio }) => ({
      key: episodio['@id'] ?? `episodio-${index}`,
      plataforma: isPlataforma(episodio.plataforma)
        ? episodio.plataforma
        : undefined,
      data: episodio.data ?? '',
      titulo: episodio.titulo?.trim() ?? '',
      descricao: episodio.descricao?.trim() ?? '',
      audio,
      link: (() => {
        const href = hrefEscolhido(episodio.link);
        return href ? flattenToAppURL(href) : '';
      })(),
      sourceIndex: index,
    }));

/**
 * Quantos itens o editor preencheu mas o `toEpisodios` recusou — vira aviso na
 * tela de edição, senão o card sumiria sem explicação.
 */
export const contarEpisodiosInvalidos = (episodios?: EpisodioItem[]): number =>
  (episodios ?? []).length - toEpisodios(episodios).length;
